import React, { useEffect, useState } from 'react';
import { adminFetch } from '../lib/extras';

// Administrace → Recenze: odznak hodnocení obchodu vedle „Vložit do košíku“ podle jazyka zákazníka.
// Společné pro hlavní adresu i všechna témata. Heureka (CZ, SK) se vyplňuje ručně, Google se načítá
// automaticky z Google Places API (Place ID + API klíč), případně ručně.

const box = 'p-4 bg-gray-50 border-2 border-gray-300 rounded-md';
const label = 'text-[10px] font-black text-gray-700 uppercase tracking-widest block';
const hint = 'text-[9px] font-bold text-gray-400 block mt-1';
const txt = 'w-full px-3 py-2 bg-white border-2 border-gray-300 rounded-md text-xs font-bold outline-none';
const btn = 'px-4 py-2 bg-white border-2 border-gray-300 rounded-md text-[10px] font-black uppercase tracking-widest hover:border-gray-500 disabled:opacity-50';
const btnDark = 'px-4 py-2 bg-black text-white border-2 border-black rounded-md text-[10px] font-black uppercase tracking-widest hover:bg-gray-800 disabled:opacity-50';

const LANG_LABEL: Record<string, string> = { cs: 'CZ', sk: 'SK', pl: 'PL', en: 'EN', de: 'DE', it: 'IT', es: 'ES', hu: 'HU' };
// Heureka jen pro české a slovenské zákazníky.
const HEUREKA_LANGS = ['cs', 'sk'];

type Entry = { source: 'off' | 'heureka' | 'google'; url: string; rating: number | null; count: number | null };
type Google = { placeId: string; url: string; rating: number | null; count: number | null };
type Cfg = {
  byLang: Record<string, Entry>; google: Google; langs: string[]; quotes: QuoteE[];
  googleLive: { placeId: string; rating: number | null; count: number | null; url: string; name?: string; at: number } | null;
  googleKey: { set: boolean; from: string; setAt: number | null };
  preview: Record<string, { source: string; host?: string; rating?: number | null; count?: number | null; url?: string }>;
  updatedAt: number | null; updatedBy: string; saved: boolean; autoPlaceId: string;
};
type QuoteE = { text: string; author: string; date: string; score: number | null | string; source: string; url: string; langs: string[] };
type Place = { id: string; name: string; address: string; rating: number | null; count: number | null; url: string };

const numStr = (v: number | null | undefined) => (v === null || v === undefined ? '' : String(v).replace('.', ','));
const dt = (ms: number | null | undefined) => (ms ? new Date(ms).toLocaleString('cs-CZ') : '');

const ReviewsAdmin: React.FC<{ isSuper: boolean }> = ({ isSuper }) => {
  const [cfg, setCfg] = useState<Cfg | null>(null);
  const [byLang, setByLang] = useState<Record<string, any>>({});
  const [google, setGoogle] = useState<any>({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [key, setKey] = useState('');
  const [query, setQuery] = useState('AAApapir Píšť');
  const [places, setPlaces] = useState<Place[] | null>(null);
  const [quotes, setQuotes] = useState<QuoteE[]>([]);

  const load = async () => {
    try {
      const c = await adminFetch<Cfg>('/api/admin/reviews');
      setCfg(c);
      setByLang(Object.fromEntries(c.langs.map(l => [l, { ...c.byLang[l], rating: numStr(c.byLang[l]?.rating), count: numStr(c.byLang[l]?.count) }])));
      setQuotes((c.quotes || []).map(q => ({ ...q, score: q.score ?? '' })));
      setGoogle({ ...c.google, rating: numStr(c.google.rating), count: numStr(c.google.count) });
    } catch (e: any) { setMsg({ ok: false, text: e.message }); }
  };
  useEffect(() => { load(); }, []);

  const run = async (fn: () => Promise<string | void>) => {
    setBusy(true); setMsg(null);
    try { const t = await fn(); if (t) setMsg({ ok: true, text: t }); await load(); }
    catch (e: any) { setMsg({ ok: false, text: e.message }); }
    finally { setBusy(false); }
  };
  const save = () => run(async () => {
    const r = await adminFetch('/api/admin/reviews', { body: { byLang, google, quotes } });
    return r.googleError ? `Uloženo. Google: ${r.googleError}` : 'Uloženo. Zákazníci uvidí změnu do 5 minut.';
  });
  const setL = (l: string, f: string, v: string) => setByLang(m => ({ ...m, [l]: { ...(m[l] || {}), [f]: v } }));

  if (!cfg) return <div className={box}>{msg ? <p className="text-xs text-red-600 font-bold">{msg.text}</p> : <p className="text-xs">Načítám…</p>}</div>;
  const gc = cfg.googleLive;

  return (
    <div className="space-y-6" data-reviews-admin>
      <div>
        <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.4em] border-b-2 border-gray-50 pb-2">Recenze obchodu v editoru</h4>
        <p className="text-[11px] text-gray-600 mt-2">Malý odznak s hodnocením vedle tlačítka „Vložit do košíku“. Platí pro hlavní adresu i všechna témata, zobrazuje se podle jazyka zákazníka. Bez vyplněného odkazu (a u Googlu bez hodnot) se nezobrazí.</p>
      </div>

      <div className={box}>
        <span className={label}>Zdroj podle jazyka</span>
        <table className="w-full text-xs mt-2">
          <thead><tr className="text-left text-[9px] uppercase text-gray-400"><th className="py-1">Jazyk</th><th>Zdroj</th><th>Odkaz na profil Heureky</th><th>Hodnocení (0–5)</th><th>Počet recenzí</th><th>Zákazník uvidí</th></tr></thead>
          <tbody>
            {cfg.langs.map(l => { const e = byLang[l] || {}; const p = cfg.preview[l]; const heureka = HEUREKA_LANGS.includes(l); return (
              <tr key={l} className="align-top border-t border-gray-200" data-review-lang={l}>
                <td className="py-2 pr-2 font-black">{LANG_LABEL[l]}</td>
                <td className="py-1 pr-2">
                  <select className={txt} value={e.source || 'off'} data-review-field="source" onChange={ev => setL(l, 'source', ev.target.value)}>
                    {heureka && <option value="heureka">Heureka</option>}
                    <option value="google">Google</option>
                    <option value="off">Nezobrazovat</option>
                  </select>
                </td>
                {e.source === 'heureka' ? (<>
                  <td className="py-1 pr-2"><input className={`${txt} font-mono`} value={e.url || ''} data-review-field="url" placeholder={l === 'sk' ? 'https://obchody.heureka.sk/…/recenze/' : 'https://obchody.heureka.cz/…/recenze/'} onChange={ev => setL(l, 'url', ev.target.value)} /></td>
                  <td className="py-1 pr-2 w-24"><input className={txt} value={e.rating || ''} data-review-field="rating" placeholder="např. 4,9" onChange={ev => setL(l, 'rating', ev.target.value)} /></td>
                  <td className="py-1 pr-2 w-28"><input className={txt} value={e.count || ''} data-review-field="count" placeholder="např. 1569" onChange={ev => setL(l, 'count', ev.target.value)} /></td>
                </>) : <td colSpan={3} className="py-2 pr-2 text-[10px] text-gray-500">{e.source === 'google' ? 'Hodnoty z Googlu níže (společné pro všechny jazyky).' : '—'}</td>}
                <td className="py-2 text-[10px]">{p?.source && p.source !== 'off' ? <a href={p.url} target="_blank" rel="noreferrer" className="underline">★ {numStr(p.rating) || '–'} · {p.count ?? '–'} · {p.host}</a> : <span className="text-gray-400">nic</span>}</td>
              </tr>
            ); })}
          </tbody>
        </table>
        <span className={hint}>Heureka nemá bez certifikátu „Ověřeno zákazníky“ export ani widget a automatické čtení stránky blokuje, proto se hodnoty opisují z profilu ručně. Náhled „Zákazník uvidí“ ukazuje uložený stav.</span>
      </div>

      <div className={box} data-reviews-google>
        <span className={label}>Google (firemní profil)</span>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
          <div>
            <label className={hint}>Place ID</label>
            <input className={`${txt} font-mono`} value={google.placeId || ''} data-google-field="placeId" placeholder={cfg.autoPlaceId ? `nalezeno samo: ${cfg.autoPlaceId}` : 'prázdné = najde se samo („AAApapir Píšť“)'} onChange={e => setGoogle((g: any) => ({ ...g, placeId: e.target.value }))} />
          </div>
          <div>
            <label className={hint}>Odkaz na recenze (prázdné = odkaz z Googlu)</label>
            <input className={`${txt} font-mono`} value={google.url || ''} data-google-field="url" placeholder="https://g.page/r/…/review" onChange={e => setGoogle((g: any) => ({ ...g, url: e.target.value }))} />
          </div>
          <div>
            <label className={hint}>Ruční hodnocení (jen bez Place ID)</label>
            <input className={txt} value={google.rating || ''} data-google-field="rating" placeholder="např. 4,3" onChange={e => setGoogle((g: any) => ({ ...g, rating: e.target.value }))} />
          </div>
          <div>
            <label className={hint}>Ruční počet recenzí (jen bez Place ID)</label>
            <input className={txt} value={google.count || ''} data-google-field="count" placeholder="např. 11" onChange={e => setGoogle((g: any) => ({ ...g, count: e.target.value }))} />
          </div>
        </div>
        <p className="text-[11px] mt-3">
          {gc ? <>Z Googlu: <strong>{gc.name || 'profil'}</strong> · ★ {numStr(gc.rating) || '–'} · {gc.count ?? '–'} recenzí · načteno {dt(gc.at)} (drží se jen v paměti serveru, obnovuje se po hodině)</> : 'Z Googlu zatím nic načteno.'}
        </p>
        <div className="flex gap-2 flex-wrap mt-2">
          <button className={btn} disabled={busy || !cfg.googleKey.set} onClick={() => run(async () => { await adminFetch('/api/admin/reviews/google-refresh', { body: {} }); return 'Hodnocení z Googlu načteno.'; })}>Načíst z Googlu teď</button>
        </div>

        <div className="mt-4 pt-3 border-t border-gray-200">
          <span className={label}>Najít Place ID</span>
          <div className="flex gap-2 mt-1">
            <input className={txt} value={query} onChange={e => setQuery(e.target.value)} />
            <button className={btn} disabled={busy || !cfg.googleKey.set} onClick={() => run(async () => { const r = await adminFetch<{ places: Place[] }>('/api/admin/reviews/google-find', { body: { query } }); setPlaces(r.places); return r.places.length ? '' : 'Nic nenalezeno.'; })}>Hledat</button>
          </div>
          {places && places.length > 0 && (
            <ul className="mt-2 space-y-1">
              {places.map(p => (
                <li key={p.id} className="flex items-center gap-2 text-[11px]">
                  <button className={btn} onClick={() => setGoogle((g: any) => ({ ...g, placeId: p.id, rating: '', count: '' }))}>Použít</button>
                  <span><strong>{p.name}</strong> – {p.address} · ★ {numStr(p.rating) || '–'} · {p.count ?? 0} recenzí</span>
                </li>
              ))}
            </ul>
          )}
          {!cfg.googleKey.set && <span className={hint}>Hledání a automatické načítání potřebuje API klíč Google Places.</span>}
        </div>

        <div className="mt-4 pt-3 border-t border-gray-200">
          <span className={label}>API klíč Google Places</span>
          <p className="text-[11px] mt-1">{cfg.googleKey.set ? `Nastaven${cfg.googleKey.from === 'env' ? ' v proměnné serveru' : cfg.googleKey.setAt ? ` (${dt(cfg.googleKey.setAt)})` : ''}.` : 'Není nastaven.'}</p>
          {isSuper && cfg.googleKey.from !== 'env' && (
            <div className="flex gap-2 mt-1">
              <input className={`${txt} font-mono`} type="password" autoComplete="off" value={key} placeholder="AIza…" onChange={e => setKey(e.target.value)} />
              <button className={btn} disabled={busy || !key} onClick={() => run(async () => { await adminFetch('/api/admin/reviews/google-key', { body: { key } }); setKey(''); return 'Klíč uložen.'; })}>Uložit klíč</button>
              {cfg.googleKey.set && <button className={btn} disabled={busy} onClick={() => run(async () => { await adminFetch('/api/admin/reviews/google-key', { body: { key: '' } }); return 'Klíč odebrán.'; })}>Odebrat</button>}
            </div>
          )}
          <span className={hint}>Klíč z Google Cloud se zapnutým „Places API (New)“, omezený jen na toto API. Ukládá se šifrovaně, mění ho jen superadministrátor.</span>
        </div>
      </div>

      <div className={box} data-reviews-quotes>
        <span className={label}>Vybrané recenze na úvodní obrazovce</span>
        <p className="text-[11px] text-gray-600 mt-1">Zobrazí se pod prvním krokem rychlého nastavení, nejvýš 3 pro daný jazyk (na mobilu 1). Text opisuj doslovně ze zdroje, zkrácení označ „…“. Recenze se nepřekládají – u jazyka zaškrtni jen ty, kde je text srozumitelný.</p>
        <div className="space-y-3 mt-3">
          {quotes.map((q, i) => {
            const set = (f: keyof QuoteE, v: any) => setQuotes(list => list.map((x, j) => j === i ? { ...x, [f]: v } : x));
            return (
              <div key={i} className="p-3 bg-white border-2 border-gray-200 rounded-md space-y-2" data-quote={i}>
                <textarea className={txt} rows={2} maxLength={400} value={q.text} data-quote-field="text" placeholder="Text recenze" onChange={e => set('text', e.target.value)} />
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                  <input className={txt} value={q.author} maxLength={60} placeholder="Jméno (prázdné = Zákazník)" onChange={e => set('author', e.target.value)} />
                  <input className={txt} type="date" value={q.date} onChange={e => set('date', e.target.value)} />
                  <input className={txt} value={q.score ?? ''} placeholder="Hodnocení v % (např. 100)" onChange={e => set('score', e.target.value)} />
                  <select className={txt} value={q.source} onChange={e => set('source', e.target.value)}><option value="heureka">Heureka</option><option value="google">Google</option></select>
                  <input className={`${txt} font-mono`} value={q.url} placeholder="Odkaz na zdroj" onChange={e => set('url', e.target.value)} />
                </div>
                <div className="flex items-center gap-3 flex-wrap text-[11px]">
                  {cfg.langs.map(l => (
                    <label key={l} className="flex items-center gap-1"><input type="checkbox" checked={q.langs.includes(l)} onChange={e => set('langs', e.target.checked ? [...q.langs, l] : q.langs.filter(x => x !== l))} />{LANG_LABEL[l]}</label>
                  ))}
                  <span className="ml-auto flex gap-1">
                    <button className={btn} disabled={i === 0} onClick={() => setQuotes(list => { const n = [...list]; [n[i - 1], n[i]] = [n[i], n[i - 1]]; return n; })}>↑</button>
                    <button className={btn} onClick={() => setQuotes(list => list.filter((_, j) => j !== i))}>Odebrat</button>
                  </span>
                </div>
              </div>
            );
          })}
        </div>
        {quotes.length < 6 && <button className={`${btn} mt-3`} onClick={() => setQuotes(list => [...list, { text: '', author: '', date: '', score: 100, source: 'heureka', url: 'https://obchody.heureka.cz/aaapapir-cz/recenze/', langs: ['cs', 'sk'] }])}>+ Přidat recenzi</button>}
      </div>

      <div className="flex items-center gap-3">
        <button className={btnDark} disabled={busy} onClick={save} data-reviews-save>Uložit recenze</button>
        {msg && <span className={`text-xs font-bold ${msg.ok ? 'text-green-700' : 'text-red-600'}`} role="status">{msg.text}</span>}
        {cfg.updatedAt && <span className="text-[10px] text-gray-400">Naposledy {dt(cfg.updatedAt)} · {cfg.updatedBy}</span>}
      </div>
    </div>
  );
};

export default ReviewsAdmin;
